import { BackButton } from "@/components/shared/BackButton";
import { DiscountedPrice } from "@/components/shared/DiscountedPrice";
import { StyledActionButton } from "@/components/shared/StyledActionButton";
import { formatReservationTime } from "@/utils/date";
import { Badge, Box, Center, Group, Stack, Text, Title, useMantineTheme } from "@mantine/core";
import { useNavigate } from "react-router-dom";

interface ReservationData {
  preferredLocationIds: string[];
  preferredTimes: string[];
  messageForGuests?: string;
  dinnerPreference: {
    preferredLanguage: string;
    preferredCost: string;
    dietaryRestrictions: string[];
    dietaryRestrictionsOtherText?: string;
  };
}

interface Props {
  onPaymentComplete: () => void;
  onBackButtonClick: () => void;
  isLoading: boolean;
  selectedDates: string[];
  selectedLocationNames: string[];
  devBypassReservationData: ReservationData;
}

export function PaymentPhase({
  onPaymentComplete,
  isLoading,
  onBackButtonClick,
  selectedDates,
  selectedLocationNames,
  devBypassReservationData,
}: Props) {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const handleDevBypass = () => {
    // Save reservation data to session storage (same as normal flow)
    sessionStorage.setItem("pendingReservation", JSON.stringify(devBypassReservationData));

    const fakePaymentId = `dev_payment_${Date.now()}`;
    navigate(`/payment/success?paymentId=${fakePaymentId}`);
  };

  return (
    <Box
      h="100%"
      // mx="lg"
      pb="lg"
      style={{
        backgroundColor: "#1A1B1E",
        overflow: "scroll",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      {/* Header */}
      <Stack pt="lg">
        <Center pos="relative" mih={50}>
          <BackButton onClick={onBackButtonClick} />
        </Center>
      </Stack>

      <Box
        mx="lg"
        px="lg"
        pb="lg"
        style={{
          backgroundColor: "#ffd7f2",
          borderRadius: "24px",
          flex: 1,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Title order={2} c="black" ta="center" my="lg">
          1회 초대장
        </Title>
        {/* Event info section */}
        <Box mb="md">
          <Group
            justify="flex-start"
            align="flex-start"
            style={{
              backgroundColor: "#c4ff35",
              borderRadius: "36px",
              border: "1px solid black",
              padding: "20px",
              position: "relative",
            }}
          >
            <Stack justify="flex-start">
              <Badge
                variant="filled"
                color="black"
                size="xl"
                radius="lg"
                styles={{ root: { padding: "16px 16px", textTransform: "none" } }}
              >
                초대 정보
              </Badge>
            </Stack>

            <Stack gap="xl">
              <Stack gap={5}>
                <Text size="clamp(0.8rem, 4vw, 1.3rem)" fw={300} c="black" ta="left">
                  요청 날짜
                </Text>
                {selectedDates.map((date, idx) => (
                  <Text key={idx} size="clamp(0.8rem, 4vw, 1.3rem)" fw={700} c="black" ta="left">
                    {formatReservationTime(date)}
                  </Text>
                ))}
              </Stack>

              <Stack gap={5}>
                <Text size="clamp(0.8rem, 4vw, 1.3rem)" fw={300} c="black" ta="left">
                  요청 장소
                </Text>
                {selectedLocationNames.map((location, idx) => (
                  <Text key={idx} size="clamp(0.8rem, 4vw, 1.3rem)" fw={700} c="black" ta="left">
                    {location}
                  </Text>
                ))}
              </Stack>
            </Stack>
          </Group>
        </Box>

        {/* Curation cost section */}
        <Box mb="xl">
          <Box
            style={{
              backgroundColor: "#4B5563",
              border: "1px solid black",
              borderRadius: "36px",
              padding: "20px",
            }}
          >
            <Group justify="space-between" align="center" px="xs">
              <Text size="xl" fw={600} c="white">
                큐레이션 비용
              </Text>
              <DiscountedPrice price={15000} discountedPrice={12000} />
            </Group>
          </Box>
        </Box>
        {/* Bullet points */}
        <Box my="xl">
          <Stack gap="md">
            <Text size="xs" c="black">
              • 오프가 더 나은 경험을 제공하고 소중한 사람들과의 약속을 지키기 위해, 각 모임에는
              큐레이션 비용이 있어요.
            </Text>
            <Text size="xs" c="black">
              • 큐레이션 비용에는 식사나 음료 비용이 포함되지 않아요.
            </Text>
            <Text size="xs" c="black">
              • 이벤트 직전 월요일 자정 이전에 취소/변경하시는 경우에 전액 환불이 가능해요.
            </Text>
            <Text size="xs" c="black">
              • 결제 시 이용약관에 동의하신 것으로 간주돼요.{" "}
              <a href="https://www.off-socials.com/terms" target="_blank">
                이용약관 확인
              </a>
            </Text>
          </Stack>
        </Box>

        <Center style={{ marginTop: "auto" }} w="100%">
          <Stack w="100%" gap="md">
            <StyledActionButton
              size="lg"
              onClick={onPaymentComplete}
              loading={isLoading}
              disabled={isLoading}
              style={{ border: "1px solid black" }}
              w="100%"
            >
              결제 & 예약 요청
            </StyledActionButton>

            {/* Dev-only bypass button */}
            {import.meta.env.DEV && (
              <StyledActionButton
                size="lg"
                onClick={handleDevBypass}
                disabled={isLoading}
                style={{
                  border: "2px solid red",
                  backgroundColor: "#ff6b6b",
                }}
                w="100%"
              >
                🚧 DEV: 결제 우회 (테스트용)
              </StyledActionButton>
            )}
          </Stack>
        </Center>
      </Box>
    </Box>
  );
}
